import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import CurrencyFacets from "./CurrencyFacets";
import { CurrencyChip, FacetHeader, FacetWrapper } from "./layout";
import {
  STATUS_GOOD,
  STATUS_CRITICAL,
  OTHER_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_MUTED,
} from "./palette";
import { formatAmount, formatCompactAmount } from "./format";

// 與 CATEGORICAL_COLORS 的色數一致：超過這個名次的項目全部併入「其他」。
const MAX_RANKED_NOTES = 8;
const OTHER_NOTE = "其他";

const SectionHeading = styled.div`
  margin: 0.5em 0 0.25em;
  font-size: 13px;
  font-weight: 600;
  color: ${TEXT_SECONDARY};
`;

const EmptyNote = styled.div`
  color: ${TEXT_MUTED};
`;

function NoteTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const { note, total, count } = payload[0].payload;

  return <ChartTooltip label={note} value={formatAmount(total)} detail={`共 ${count} 筆`} />;
}

NoteTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

NoteTooltip.defaultProps = {
  active: false,
  payload: [],
};

// 「其他」是前端把超過前 8 名的 note 匯總而成，沒有對應單一 note 條件可查，因此不可點擊 drill-down。
export function isDrillableNote(note) {
  return note !== OTHER_NOTE;
}

// 只取金額最高的前 8 筆，其餘併入「其他」，避免琳瑯滿目的備註文字塞爆圖表。
export function rankNotes(rows) {
  const sorted = [...rows].sort((a, b) => b.total - a.total);
  const ranked = sorted.slice(0, MAX_RANKED_NOTES);
  const rest = sorted.slice(MAX_RANKED_NOTES);

  if (rest.length === 0) {
    return ranked;
  }

  const otherTotal = rest.reduce((sum, row) => sum + row.total, 0);
  const otherCount = rest.reduce((sum, row) => sum + row.count, 0);

  return [...ranked, { note: OTHER_NOTE, total: otherTotal, count: otherCount }];
}

function CurrencyNoteChart({ currencyName, currencyId, rows, barColor, categoryType, onSelect }) {
  const data = useMemo(() => {
    const currencyTotal = rows.reduce((sum, row) => sum + row.total, 0);

    return rankNotes(rows).map((row) => ({
      note: row.note,
      total: row.total,
      count: row.count,
      share: currencyTotal > 0 ? Math.round((row.total / currencyTotal) * 100) : 0,
    }));
  }, [rows]);

  const height = Math.max(data.length * 40, 80);

  function handleClick(clickData) {
    const entry = clickData && clickData.payload ? clickData.payload : clickData;
    if (!onSelect || !entry || !isDrillableNote(entry.note)) {
      return;
    }
    onSelect({
      kind: "note",
      note: entry.note,
      categoryType,
      currencyId,
      currencyName,
      label: entry.note,
    });
  }

  return (
    <FacetWrapper>
      <FacetHeader>
        <CurrencyChip>{currencyName}</CurrencyChip>
      </FacetHeader>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 72, left: 8, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="note"
            tick={{ fill: TEXT_PRIMARY, fontSize: 13 }}
            axisLine={false}
            tickLine={false}
            width={90}
          />
          <Tooltip content={<NoteTooltip />} cursor={{ fill: "rgba(11,11,11,0.04)" }} />
          <Bar dataKey="total" radius={[0, 4, 4, 0]} maxBarSize={24} onClick={handleClick}>
            {data.map((entry) => (
              <Cell
                key={entry.note}
                fill={entry.note === OTHER_NOTE ? OTHER_COLOR : barColor}
                cursor={onSelect && isDrillableNote(entry.note) ? "pointer" : "default"}
              />
            ))}
            <LabelList
              dataKey={(entry) => `${formatCompactAmount(entry.total)} (${entry.share}%)`}
              position="right"
              style={{ fill: TEXT_PRIMARY, fontSize: 12 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </FacetWrapper>
  );
}

CurrencyNoteChart.propTypes = {
  currencyName: PropTypes.string.isRequired,
  currencyId: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  barColor: PropTypes.string.isRequired,
  categoryType: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
};

CurrencyNoteChart.defaultProps = {
  onSelect: null,
};

function BreakdownSection({ heading, rows, barColor, categoryType, onSelect }) {
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <>
      <SectionHeading>{heading}</SectionHeading>
      <CurrencyFacets rows={rows}>
        {(currency, currencyRows) => (
          <CurrencyNoteChart
            key={currency.id}
            currencyName={currency.name}
            currencyId={currency.id}
            rows={currencyRows}
            barColor={barColor}
            categoryType={categoryType}
            onSelect={onSelect}
          />
        )}
      </CurrencyFacets>
    </>
  );
}

BreakdownSection.propTypes = {
  heading: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  barColor: PropTypes.string.isRequired,
  categoryType: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
};

BreakdownSection.defaultProps = {
  onSelect: null,
};

export default function NoteSpendingChart({ noteSpending, onSelect }) {
  const rows = noteSpending || [];
  const expenseRows = rows.filter((row) => row.type !== "income");
  const incomeRows = rows.filter((row) => row.type === "income");

  if (rows.length === 0) {
    return <EmptyNote>目前沒有收支資料可供分析。</EmptyNote>;
  }

  return (
    <>
      <BreakdownSection
        heading="支出"
        rows={expenseRows}
        barColor={STATUS_CRITICAL}
        categoryType="expense"
        onSelect={onSelect}
      />
      <BreakdownSection
        heading="收入"
        rows={incomeRows}
        barColor={STATUS_GOOD}
        categoryType="income"
        onSelect={onSelect}
      />
    </>
  );
}

NoteSpendingChart.propTypes = {
  noteSpending: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
};

NoteSpendingChart.defaultProps = {
  noteSpending: null,
  onSelect: null,
};
